/**
 * Module dependencies.
 */

import { QueryOptions } from 'app/types/query-options';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { isEmpty } from 'lodash';
import { template } from './template';
import config from 'config';
import qs from 'qs';
import urlJoin from 'url-join';
import { generatePath } from 'react-router-dom';
import { RequestOptions } from 'app/types/network';

/**
 * `EndpointRequestOptions` type.
 */

export type EndpointRequestOptions = {
  interpolations?: Record<string, any>;
  params?: QueryOptions | RequestOptions | Record<string, any>;
};

/**
 * Export `EndpointKey` type — dot-notation paths to all leaf keys of `apiEndpoints`.
 */

type FlatKeys<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : FlatKeys<T[K] & object, `${P}${K}.`>;
}[keyof T & string];

export type EndpointKey = FlatKeys<typeof apiEndpoints>;

/**
 * Export `EndpointValue` type — all values and nested values of apiEndpoints
 */

type DeepValues<T> = { [K in keyof T]: T[K] extends string ? T[K] : DeepValues<T[K]> }[keyof T];

export type EndpointValue = DeepValues<typeof apiEndpoints>;

/**
 * Get endpoint.
 */

function getEndpoint(name: EndpointKey | EndpointValue, params?: any): string {
  const endpoint = (apiEndpoints as any)[name] ?? name;

  if (!params) {
    return endpoint;
  }

  return template(endpoint, params);
}

/**
 * Resolve url.
 */

function resolveUrl(baseUrl: string, url: string): string {
  if (!baseUrl || /^https?:\/\//.test(url)) {
    return url;
  }

  return urlJoin(baseUrl, url);
}

/**
 * Export `resolveAppPath`.
 */

export function resolveAppPath(url: string, params: any) {
  return generatePath(url, params);
}

/**
 * Resolve url.
 */

export function handleQueryParams(url: string, params: Record<string, any>): string {
  if (!params || isEmpty(params)) {
    return url;
  }

  return urlJoin(url, `?${qs.stringify(params)}`);
}

/**
 * Export `resolveAppUrl` util.
 */

export function resolveAppUrl(routeKey: EndpointKey | EndpointValue, options?: EndpointRequestOptions): string {
  const { interpolations, params } = options ?? {};
  let endpointUrl = getEndpoint(routeKey, interpolations);

  if (params) {
    endpointUrl = handleQueryParams(endpointUrl, params);
  }

  return resolveUrl(config?.api?.baseUrl, endpointUrl);
}

/**
 * Export `resolveBaseEndpointUrl` util.
 */

export function resolveBaseEndpointUrl(url: string): string {
  return resolveUrl(config?.api?.baseUrl, url);
}
